import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, EmptyState, Modal, Field, Button, inr } from "../components/UI";
import { Layers, Plus, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

const blank = { product_id: "", name: "", attributes: {}, cost_price: 0, delivery_cost: 0, notes: "", is_active: true };

export default function Variants() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [attrPairs, setAttrPairs] = useState([{ k: "", v: "" }]);

  const load = async () => {
    try {
      const [v, p] = await Promise.all([api.get("/variants"), api.get("/products")]);
      setList(v.data); setProducts(p.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...blank, product_id: products[0]?.id || "" });
    setAttrPairs([{ k: "Size", v: "" }, { k: "Paper", v: "" }, { k: "", v: "" }]);
    setOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({ ...v });
    const pairs = Object.entries(v.attributes || {}).map(([k, val]) => ({ k, v: String(val) }));
    if (pairs.length === 0) pairs.push({ k: "", v: "" });
    setAttrPairs(pairs);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const attributes = {};
    attrPairs.forEach((p) => { if (p.k.trim() && p.v.trim()) attributes[p.k.trim()] = p.v.trim(); });
    const payload = { ...form, attributes, cost_price: Number(form.cost_price), delivery_cost: Number(form.delivery_cost) };
    try {
      if (editing) { await api.put(`/variants/${editing.id}`, payload); toast.success("Variant updated"); }
      else { await api.post("/variants", payload); toast.success("Variant added"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"?`)) return;
    try { await api.delete(`/variants/${v.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = filter ? list.filter((v) => v.product_id === filter) : list;

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="variants-page">
      <PageHeader
        title="Product Variants & Attributes"
        subtitle="Pre-configure cost prices for size/paper/finish combos — used by the Pricing Calculator"
        action={isAdmin && <Button onClick={openNew} data-testid="add-variant-btn"><Plus size={16}/>Add Variant</Button>}
      />

      <div className="flex flex-wrap gap-2 mb-5">
        <button data-testid="vf-all" onClick={() => setFilter("")} className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold ${!filter ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569]"}`}>All Products</button>
        {products.map((p) => (
          <button key={p.id} data-testid={`vf-${p.id}`} onClick={() => setFilter(p.id)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold ${filter === p.id ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"}`}>
            {p.name}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Layers} title="No variants yet"
          hint="Define product variants like 'Hospital File - A4 / 300 GSM / Hard Bind' with their exact cost price. Then the Pricing Calculator can instantly suggest L1/L2/L3 rates."
          action={isAdmin && <Button onClick={openNew}><Plus size={16}/> Add First Variant</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="variants-grid">
          {filtered.map((v) => (
            <div key={v.id} className="card-flat p-4" data-testid={`var-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-[#C1121F] font-semibold">{v.product_name}</div>
                  <h3 className="font-display font-bold text-base leading-tight mt-0.5">{v.name}</h3>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(v)} className="p-1 text-[#475569] hover:text-[#C1121F]"><Pencil size={13}/></button>
                    <button onClick={() => remove(v)} className="p-1 text-[#475569] hover:text-red-600"><Trash2 size={13}/></button>
                  </div>
                )}
              </div>
              {v.attributes && Object.keys(v.attributes).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {Object.entries(v.attributes).map(([k, val]) => (
                    <span key={k} className="text-[10px] px-2 py-0.5 bg-[#F8F9FA] rounded text-[#475569]">
                      <b>{k}:</b> {val}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-[#E5E7EB] grid grid-cols-2 gap-2 text-sm">
                {isAdmin ? (
                  <>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold flex items-center gap-1"><Lock size={8}/> Cost</div>
                      <div className="font-mono-num font-bold text-[#0F172A]">{inr(v.cost_price)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] font-semibold">Delivery</div>
                      <div className="font-mono-num font-semibold text-[#475569]">{inr(v.delivery_cost || 0)}</div>
                    </div>
                  </>
                ) : (
                  <div className="col-span-2 text-[11px] text-[#94A3B8] italic">Cost details visible to admin only</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Variant" : "Add Variant"} maxWidth="max-w-2xl">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product" required>
              <select data-testid="var-product" required value={form.product_id} onChange={(e) => setForm({...form, product_id: e.target.value})} className="input-base">
                <option value="">Select…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Variant Name" required>
              <input data-testid="var-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-base" placeholder="e.g. A4 / 300 GSM / Hard Bind" />
            </Field>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-1.5 block">Attributes</label>
            <div className="space-y-2">
              {attrPairs.map((pair, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input placeholder="Attribute (e.g. Paper)" value={pair.k} onChange={(e) => { const arr = [...attrPairs]; arr[i].k = e.target.value; setAttrPairs(arr); }} className="input-base col-span-5 text-sm" />
                  <input placeholder="Value (e.g. 300 GSM)" value={pair.v} onChange={(e) => { const arr = [...attrPairs]; arr[i].v = e.target.value; setAttrPairs(arr); }} className="input-base col-span-6 text-sm" />
                  <button type="button" onClick={() => setAttrPairs(attrPairs.filter((_, idx) => idx !== i))} className="col-span-1 text-red-500 p-2"><Trash2 size={14}/></button>
                </div>
              ))}
              <button type="button" onClick={() => setAttrPairs([...attrPairs, { k: "", v: "" }])} className="text-xs text-[#C1121F] font-semibold hover:underline">+ Add attribute</button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Cost Price (₹ / unit)" required>
              <input data-testid="var-cost" type="number" step="0.01" required value={form.cost_price} onChange={(e) => setForm({...form, cost_price: e.target.value})} className="input-base" />
            </Field>
            <Field label="Delivery Cost (₹ / unit)">
              <input data-testid="var-delivery" type="number" step="0.01" value={form.delivery_cost} onChange={(e) => setForm({...form, delivery_cost: e.target.value})} className="input-base" />
            </Field>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} placeholder="Anything special — supplier, machine, finish details…" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-variant-btn">{editing ? "Save" : "Add Variant"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
